/**
 * App Context
 * Manages global app state like metrics refresh triggers and pre-fetched graph data
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { realAPI } from '../api/realEndpoints';

interface AppContextType {
  refreshTrigger: number;
  triggerRefresh: () => void;
  dashboardData: {
    metrics: any;
    usersGraph: any;
    bookingsGraph: any;
  } | null;
  preloadDashboardData: () => Promise<void>;
  clearDashboardData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [dashboardData, setDashboardData] = useState<{
    metrics: any;
    usersGraph: any;
    bookingsGraph: any;
  } | null>(null);

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const preloadDashboardData = useCallback(async () => {
    try {
      const [metricsResponse, usersGraphResponse, bookingsGraphResponse] = await Promise.all([
        realAPI.dashboard.getMetrics(),
        realAPI.dashboard.getUsersGraph('daily', undefined, undefined, undefined),
        realAPI.dashboard.getBookingsGraph('daily', undefined, undefined)
      ]);

      setDashboardData({
        metrics: metricsResponse,
        usersGraph: usersGraphResponse,
        bookingsGraph: bookingsGraphResponse
      });
    } catch (error) {
      console.error('❌ Failed to preload dashboard data:', error);
      // Don't throw - just log the error and continue
    }
  }, []);

  const clearDashboardData = useCallback(() => {
    setDashboardData(null);
  }, []);

  return (
    <AppContext.Provider value={{
      refreshTrigger,
      triggerRefresh,
      dashboardData,
      preloadDashboardData,
      clearDashboardData
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
